// src/store/useChatStore.ts
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

// Add storage key constant
const STORAGE_KEY = 'chat_messages';
const WELCOME_SEEN_KEY = 'chat_welcome_seen';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

interface ChatState {
  isOpen: boolean;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  retryCount: number;
  lastMessage: string;
  toggleChat: () => void;
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  sendMessage: (content: string) => Promise<void>;
  clearError: () => void;
  retrySendMessage: () => Promise<void>;
  clearChat: () => void;
  loadMessages: () => void;
}

export const useChatStore = create<ChatState>((set, get) => {
  // Load messages from localStorage on store creation
  const loadFromStorage = (): Message[] => {
    if (typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved).map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      })) : [];
    } catch (error) {
      console.error('Failed to load messages:', error);
      return [];
    }
  };

  // Initial state
  const initialState = {
    isOpen: false,
    messages: [] as Message[],
    isLoading: false,
    error: null as string | null,
    retryCount: 0,
    lastMessage: '',
  };

  return {
    ...initialState,

    toggleChat: () => set((state) => ({ 
      isOpen: !state.isOpen,
      ...(state.isOpen ? {} : { error: null, retryCount: 0 })
    })),

    addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => {
      const newMessage = {
        ...message,
        id: uuidv4(),
        timestamp: new Date(),
      };
      set((state) => {
        const updatedMessages = [...state.messages, newMessage];
        // Save to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedMessages));
        }
        return { messages: updatedMessages };
      });
    },

    clearError: () => set({ error: null, retryCount: 0 }),

    clearChat: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(WELCOME_SEEN_KEY);
      }
      set({ 
        messages: [],
        error: null,
        retryCount: 0,
        lastMessage: ''
      });
    },

    loadMessages: () => {
      const messages = loadFromStorage();
      set({ messages });
      
      // Add welcome message if first time
      if (typeof window !== 'undefined' && !localStorage.getItem(WELCOME_SEEN_KEY) && messages.length === 0) {
        const store = get();
        store.addMessage({
          content: "Hello! I'm your AI assistant. How can I help you today?",
          role: 'assistant',
        });
        localStorage.setItem(WELCOME_SEEN_KEY, 'true');
      }
    },

    sendMessage: async (content: string) => {
      const { addMessage, messages } = get();
      
      // Add user message
      addMessage({
        content,
        role: 'user',
      });

      set({ 
        isLoading: true, 
        error: null, 
        lastMessage: content 
      });

      try {
        // Convert messages to the format expected by Gemini
        // Only include the most recent messages to avoid context window issues
        // Ensure first message is from user and map 'assistant' role to 'model' for Gemini
        const history = messages
          .slice(-10) // Limit history to last 10 messages
          .map(msg => ({
            role: msg.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: msg.content }],
          }));

        // Ensure first message is from user
        if (history.length > 0 && history[0].role === 'model') {
          history.shift(); // Remove the first message if it's from the assistant
        }

        // If no history, include current message as user message
        if (history.length === 0) {
          history.push({
            role: 'user',
            parts: [{ text: content }]
          });
        }

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            content, 
            history 
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || 'Failed to get response from server';
          throw new Error(
            response.status === 429 
              ? 'Too many requests. Please try again later.'
              : errorMessage
          );
        }

        const data = await response.json();
        addMessage({
          content: data.response,
          role: 'assistant',
        });
        set({ retryCount: 0 });
      } catch (error) {
        console.error('Error sending message:', error);
        set(state => ({ 
          error: error instanceof Error ? error.message : 'Failed to send message',
          retryCount: state.retryCount + 1,
        }));
      } finally {
        set({ isLoading: false });
      }
    },

    retrySendMessage: async () => {
      const { lastMessage, sendMessage } = get();
      if (lastMessage) {
        return sendMessage(lastMessage);
      }
    }
  };
});

// Initialize store with messages from localStorage
if (typeof window !== 'undefined') {
  useChatStore.getState().loadMessages();
}